<template>
  <main class="main">
    <div class="container">
      <LazyUiBreadCrumbsV2
        :items="[
          {
            title: t('teams_label') || '',
            link: localePath('/teams'),
          },
        ]"
      />
      <PagesTeams
        v-model:search="searchText"
        :data="data?.data?.getProTeams || {} as PaginatedProTeams"
        :loading="status === 'pending'"
        @update:search="onSearch"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { PaginatedProTeams } from '~/graphql/generated/schema-types';
import { useAlternateLinks } from '~/composables/alternate-links';
import { getTeams } from '~/utils/api';

const localePath = useLocalePath();
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const searchText = ref('');
const limit = ref(18);
const page = computed(() =>
  route.query.page ? Number.parseInt(route.query.page as string) : 1);

const { error, data, status } = await useAsyncData('getTeams', () => getTeams({
  limit: limit.value,
  page: page.value,
}, locale.value, searchText.value), { watch: [page, searchText, locale] });

if (error.value || data?.value?.errors?.length) {
  throw createError({
    status: 404,
    statusText: `Teams page error: ${error.value || data?.value?.errors?.[0]?.message || ''}`,
  });
}

watch(page, () => {
  if (process.client) {
    window.scrollTo(0, 0);
  }
});

function onSearch() {
  router.push({
    query: {
      ...route.query,
      page: 1,
    },
  });
}

const title = t('heads.teams.title');
const description = t('heads.teams.description');
// const weaponIcon = 'https://csmarketcap.com/img/logo-bg-black.png';

const {
  alternateLinks,
  canonical,
} = useAlternateLinks('/teams');

useHead({
  title: () => title,
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title,
  ogTitle: () => title,
  description: () => description,
  ogDescription: () => description,
  // ogImage: () => weaponIcon,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title,
  twitterDescription: () => description,
  // twitterImage: () => weaponIcon,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      {
        name: title,
        item: localePath('/teams'),
      },
    ],
  }),
]);
</script>
